<template>
  <div
    ref="dialogRoot"
    class="avatar-chat"
    role="dialog"
    aria-modal="true"
    aria-labelledby="avatar-chat-title"
    tabindex="-1"
    @keydown.esc="handleEscape"
  >
    <div class="chat-container">
      <div class="header-section">
        <h2 id="avatar-chat-title">AMIS 智能助手</h2>
        <p class="subtitle">與我們的AI數位人互動，了解AMIS的專業服務</p>
        <button class="close-btn" type="button" aria-label="關閉對話" @click="requestClose"><span aria-hidden="true">×</span></button>
      </div>

      <div class="chat-section">
        <div class="avatar-wrapper">
          <ModelViewer
            ref="modelViewer"
            :model-url="'/models/default.glb'"
            :emotion="currentEmotion"
            :action="currentAction"
          />
        </div>

        <div class="controls-panel">
          <div class="greeting-section" v-if="!hasStarted">
            <h3>歡迎來到 AMIS</h3>
            <p>我係您嘅專業服務助手，可以為您介紹AMIS嘅全球商業服務同創新科技方案。按下「開始對話」即刻同我傾計！</p>
            <button class="start-btn" @click="startConversation" :disabled="isLoading">
              <span v-if="isLoading">載入中...</span>
              <span v-else>開始對話</span>
            </button>
          </div>

          <div class="conversation-section" v-else>
            <div class="messages" ref="messagesContainer" role="log" aria-live="polite" aria-label="對話內容">
              <ChatMessageWithLinks
                v-for="message in messages" 
                :key="message.id"
                :message="message"
                @action="handleMessageAction"
              />
            </div>

            <div class="input-section">
              <div class="input-group">
                <textarea 
                  v-model="userInput"
                  :aria-label="isCantonese ? '輸入訊息' : 'Type your message'"
                  :placeholder="isCantonese ? '請問AMIS嘅服務...' : 'Ask about AMIS services...'"
                  :disabled="isProcessing"
                  @keydown.enter.prevent="sendMessage"
                  rows="3"
                ></textarea>
                <div class="control-buttons">
                  <button class="lang-toggle" type="button" @click="toggleLanguage" :title="isCantonese ? '切換至英語' : 'Switch to Cantonese'" :aria-label="isCantonese ? '切換至英語' : 'Switch to Cantonese'">
                    {{ isCantonese ? 'EN' : '粵' }}
                  </button>
                  <button @click="sendMessage" :disabled="isProcessing || !userInput.trim()" class="send-btn">
                    <span v-if="isProcessing">處理中...</span>
                    <span v-else>發送</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <audio ref="audioPlayer" style="display: none;" @ended="onAudioEnded"></audio>
        </div>
      </div>
    </div>

    <!-- Close Confirmation Modal -->
    <div class="modal-overlay" v-if="showConfirmModal" @click="hideConfirmation">
      <div class="confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="avatar-confirm-title" @click.stop>
        <h3 id="avatar-confirm-title">結束對話？</h3>
        <p>您即將關閉與AI助手的對話。所有對話紀錄將會遺失，無法復原。</p>
        <div class="modal-buttons">
          <button class="cancel-btn" @click="hideConfirmation">繼續對話</button>
          <button class="confirm-btn" @click="closeConversation">結束對話</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import ModelViewer from './ModelViewer.vue'
import ChatMessageWithLinks from './ChatMessageWithLinks.vue'
import { azureTtsService } from '../services/azureTtsService.js'
import { openaiChatService } from '../services/openaiChatService.js'
import { API_CONFIG, validateApiConfig, getConfigSummary } from '../services/apiConfig.js'
import { apiTestSuite } from '../utils/apiTest.js'

// Emit event for parent component
const emit = defineEmits(['close'])

const modelViewer = ref(null)
const audioPlayer = ref(null)
const messagesContainer = ref(null)
const dialogRoot = ref(null)
let previouslyFocused = null

const hasStarted = ref(false)
const isLoading = ref(false)
const isProcessing = ref(false)
const currentEmotion = ref('')
const currentAction = ref('Idle')
const isCantonese = ref(true) // Default to Cantonese

// Configuration state - 使用API_CONFIG中的配置
const config = ref({
  enhancedSSML: API_CONFIG.debug.enhancedSSML,
  debugLogs: API_CONFIG.debug.enableLogs,
  defaultVoice: API_CONFIG.voices.cantonese.female, // 默认粤语女声
  voices: API_CONFIG.voices
})
const userInput = ref('')
const messages = ref([])
const showConfirmModal = ref(false)

const greetingMessages = {
  cantonese: "您好！我係AMIS嘅智能助手，好高興為您服務。我哋AMIS致力於提供專業嘅全球商業服務同創新科技方案，包括AI數位人等尖端技術應用。有咩可以幫到您呢？",
  english: "Hello! I'm AMIS's intelligent assistant, pleased to serve you. We at AMIS are committed to providing professional global business services and innovative technology solutions, including cutting-edge applications like AI digital humans. How can I help you today?"
}

function toggleLanguage() {
  isCantonese.value = !isCantonese.value
  // Update voice based on language - using female voices as default
  config.value.defaultVoice = isCantonese.value 
    ? config.value.voices.cantonese.female 
    : config.value.voices.english.female
}

async function startConversation() {
  isLoading.value = true
  
  try {
    const greeting = isCantonese.value ? greetingMessages.cantonese : greetingMessages.english
    
    // Add greeting message
    const greetingMsg = {
      id: Date.now().toString(),
      type: 'assistant',
      content: greeting,
      timestamp: new Date()
    }
    
    messages.value.push(greetingMsg)
    hasStarted.value = true
    
    // Start avatar animation
    currentAction.value = 'Talking'
    currentEmotion.value = 'cheerful'
    
    // Synthesize and play greeting
    await synthesizeAndPlay(greeting)
    
    // Scroll to messages
    await nextTick()
    scrollToBottom()
    
  } catch (error) {
    console.error('Failed to start conversation:', error)
  } finally {
    isLoading.value = false
  }
}

async function sendMessage() {
  if (!userInput.value.trim() || isProcessing.value) return
  
  const userMessage = {
    id: Date.now().toString(),
    type: 'user',
    content: userInput.value.trim(),
    timestamp: new Date()
  }
  
  messages.value.push(userMessage)
  const currentInput = userInput.value.trim()
  userInput.value = ''
  
  await nextTick()
  scrollToBottom()
  
  isProcessing.value = true
  
  try {
    // Generate response using a simple AMIS-focused response system
    const responseData = await generateResponse(currentInput)
    
    const assistantMessage = {
      id: Date.now().toString() + '_assistant',
      type: 'assistant',
      content: responseData.response,
      timestamp: new Date(),
      actions: responseData.actions
    }
    
    messages.value.push(assistantMessage)
    
    // Update avatar emotion based on response content
    updateEmotionBasedOnContent(responseData.response)
    currentAction.value = 'Talking'
    
    // Synthesize and play response
    await synthesizeAndPlay(responseData.response)
    
    await nextTick()
    scrollToBottom()
    
  } catch (error) {
    console.error('Failed to process message:', error)
    const errorMessage = {
      id: Date.now().toString() + '_error',
      type: 'assistant',
      content: isCantonese.value ? "唔好意思，我而家處理您嘅請求遇到咗啲問題，請稍後再試。" : "I apologize, but I'm having trouble processing your request right now. Please try again.",
      timestamp: new Date()
    }
    messages.value.push(errorMessage)
  } finally {
    isProcessing.value = false
  }
}

async function generateResponse(userMessage) {
  try {
    console.log('🤖 调用真实OpenAI服务:', {
      message: userMessage.slice(0, 100),
      language: isCantonese.value ? 'Cantonese' : 'English',
      conversation_length: messages.value.length
    });

    // 使用真实的OpenAI服务生成智能回复
    const language = isCantonese.value ? 'cantonese' : 'english'
    const responseData = await openaiChatService.generateIntelligentResponse(
      userMessage, 
      language,
      {
        conversationLength: messages.value.length,
        currentEmotion: currentEmotion.value
      }
    )

    if (responseData.error) {
      console.warn('⚠️ OpenAI服务返回错误，使用后备回复')
    }

    return responseData

  } catch (error) {
    console.error('❌ OpenAI服务调用失败，使用后备系统:', error)
    return generateFallbackResponse(userMessage)
  }
}

// 后备回复系统（当OpenAI服务不可用时使用）
function generateFallbackResponse(userMessage) {
  console.log('🔄 使用后备回复系统')
  
  // 增强的关键词回复系统（后备方案）
  const message = userMessage.toLowerCase()
  
  // Check for common keywords and provide appropriate responses
  if (message.includes('service') || message.includes('服務') || message.includes('business') || message.includes('商業')) {
    return {
      response: isCantonese.value 
        ? "AMIS提供多元化嘅專業服務，包括全球商業諮詢、旅遊服務、同創新科技方案。我哋特別專長於AI數位人技術，為客戶帶來嶄新嘅互動體驗。您想了解邊方面嘅服務呢？"
        : "AMIS provides diverse professional services including global business consulting, travel services, and innovative technology solutions. We specialize in AI digital human technology, bringing cutting-edge interactive experiences to our clients. Which aspect of our services would you like to learn more about?",
      actions: [
        {
          id: 'learn_services',
          type: 'link',
          label: isCantonese.value ? '了解服務' : 'Learn Services',
          style: 'primary',
          url: '/service'
        }
      ]
    }
  }
  
  if (message.includes('contact') || message.includes('聯絡') || message.includes('聯繫')) {
    return {
      response: isCantonese.value 
        ? "歡迎聯絡AMIS！您可以透過我哋嘅聯絡頁面搵到詳細嘅聯繫資訊，包括電話、電郵同地址。我哋嘅專業團隊隨時為您服務。"
        : "Welcome to contact AMIS! You can find detailed contact information including phone, email, and address on our contact page. Our professional team is ready to serve you at any time.",
      actions: [
        {
          id: 'contact_us',
          type: 'link',
          label: isCantonese.value ? '聯絡我們' : 'Contact Us',
          style: 'success',
          url: '/contact'
        }
      ]
    }
  }
  
  if (message.includes('about') || message.includes('關於') || message.includes('公司') || message.includes('history') || message.includes('歷史')) {
    return {
      response: isCantonese.value 
        ? "AMIS係一間專業嘅國際服務公司，擁有超過十年嘅行業經驗。我哋致力於為全球旅客同企業客戶提供高品質嘅服務，並積極引入AI數位人等創新技術，為客戶創造更好嘅體驗。我哋嘅團隊遍佈全球五十多個國家，服務超過一千個合作夥伴。"
        : "AMIS is a professional international service company with over ten years of industry experience. We are committed to providing high-quality services to global travelers and corporate clients, actively introducing innovative technologies like AI digital humans to create better experiences for our customers. Our team spans over 50 countries, serving more than 1,000 partners.",
      actions: [
        {
          id: 'about_us',
          type: 'link',
          label: isCantonese.value ? '關於我們' : 'About Us',
          style: 'secondary',
          url: '/about'
        }
      ]
    }
  }
  
  // AI and technology related queries
  if (message.includes('ai') || message.includes('人工智能') || message.includes('數位人') || message.includes('technology') || message.includes('科技')) {
    return {
      response: isCantonese.value 
        ? "我哋嘅AI數位人技術係AMIS嘅核心優勢之一！我哋利用最先進嘅人工智能技術，為客戶提供個性化嘅智能客服體驗。數位人可以用多種語言溝通，包括粵語同英語，為您提供24/7嘅專業支援。呢項技術已經幫助我哋嘅客戶大幅提升服務效率同客戶滿意度。"
        : "Our AI digital human technology is one of AMIS's core advantages! We utilize cutting-edge artificial intelligence to provide personalized intelligent customer service experiences. Our digital humans can communicate in multiple languages, including Cantonese and English, offering 24/7 professional support. This technology has significantly helped our clients improve service efficiency and customer satisfaction.",
      actions: [
        {
          id: 'tech_demo',
          type: 'custom',
          label: isCantonese.value ? '體驗更多功能' : 'Experience More Features',
          style: 'success',
          data: { action: 'show_features' }
        }
      ]
    }
  }
  
  // Travel and tourism related queries
  if (message.includes('travel') || message.includes('旅遊') || message.includes('tourism') || message.includes('trip') || message.includes('旅行')) {
    return {
      response: isCantonese.value 
        ? "AMIS提供專業嘅旅遊服務！我哋幫助全球旅客規劃完美嘅旅程，提供個人化嘅旅遊方案、酒店預訂、交通安排同當地體驗活動。無論您想去邊度，我哋都有經驗豐富嘅團隊為您服務。您有咩特別嘅旅遊需求呢？"
        : "AMIS provides professional travel services! We help global travelers plan perfect journeys, offering personalized travel solutions, hotel bookings, transportation arrangements, and local experiences. Wherever you want to go, we have an experienced team to serve you. What specific travel needs do you have?",
      actions: [
        {
          id: 'travel_services',
          type: 'link',
          label: isCantonese.value ? '旅遊服務' : 'Travel Services',
          style: 'primary',
          url: '/service'
        }
      ]
    }
  }
  
  // Greeting responses
  if (message.includes('hello') || message.includes('hi') || message.includes('你好') || message.includes('哈囉') || message.includes('嗨')) {
    return {
      response: isCantonese.value 
        ? "您好呀！歡迎同AMIS嘅AI助手傾談！我可以為您介紹我哋嘅服務，回答關於商業諮詢、旅遊安排或者AI數位人技術嘅問題。有咩我可以幫到您？"
        : "Hello there! Welcome to chat with AMIS's AI assistant! I can introduce our services and answer questions about business consulting, travel arrangements, or AI digital human technology. How can I help you?"
    }
  }
  
  // Price and cost inquiries
  if (message.includes('price') || message.includes('cost') || message.includes('價錢') || message.includes('收費') || message.includes('費用') || message.includes('錢')) {
    return {
      response: isCantonese.value 
        ? "我哋AMIS提供透明嘅定價方案！不同服務有唔同嘅收費結構，我哋會根據您嘅具體需求提供詳細報價。建議您直接聯絡我哋嘅專業團隊，佢哋會為您度身訂造最適合嘅方案同報價。"
        : "We at AMIS offer transparent pricing plans! Different services have different fee structures, and we provide detailed quotes based on your specific needs. I recommend contacting our professional team directly - they'll create the most suitable plan and pricing for you.",
      actions: [
        {
          id: 'get_quote',
          type: 'link',
          label: isCantonese.value ? '獲取報價' : 'Get Quote',
          style: 'success',
          url: '/contact'
        }
      ]
    }
  }
  
  // Thank you responses
  if (message.includes('thank') || message.includes('thanks') || message.includes('謝謝') || message.includes('多謝')) {
    return {
      response: isCantonese.value 
        ? "唔使客氣！好開心能夠幫到您。如果您之後有任何關於AMIS服務嘅問題，隨時都可以搵我哋。祝您有美好嘅一天！"
        : "You're very welcome! I'm happy I could help you. If you have any questions about AMIS services in the future, feel free to contact us anytime. Have a wonderful day!"
    }
  }
  
  // Default response with more personality
  const defaultResponses = {
    cantonese: [
      "多謝您嘅查詢！我係AMIS嘅AI助手，專門為客戶提供服務諮詢。您可以問我關於商業服務、旅遊安排、或者AI技術嘅問題。有咩特別想了解嘅呢？",
      "好有趣嘅問題！雖然我未能完全理解您嘅意思，但我可以為您介紹AMIS嘅各種服務。我哋係一間專業嘅國際服務公司，有豐富經驗幫助客戶解決各種需求。",
      "讓我諗諗點樣最好咁回答您...AMIS有好多不同嘅服務可以幫到您。不如您話俾我聽，您最想了解我哋邊方面嘅服務？"
    ],
    english: [
      "Thank you for your inquiry! I'm AMIS's AI assistant, specialized in providing service consultations. You can ask me about business services, travel arrangements, or AI technology. What would you particularly like to know?",
      "That's an interesting question! While I might not fully understand your specific meaning, I can introduce you to AMIS's various services. We're a professional international service company with rich experience helping clients solve various needs.",
      "Let me think about the best way to answer you... AMIS has many different services that can help you. How about you tell me which aspect of our services you'd most like to learn about?"
    ]
  }
  
  // Randomly select a default response for more natural conversation
  const responses = isCantonese.value ? defaultResponses.cantonese : defaultResponses.english
  const randomResponse = responses[Math.floor(Math.random() * responses.length)]
  
  return {
    response: randomResponse
  }
}

async function synthesizeAndPlay(text) {
  try {
    console.log('🎤 开始Azure TTS语音合成:', { 
      text: text.slice(0, 50), 
      voice: config.value.defaultVoice,
      language: isCantonese.value ? 'Cantonese' : 'English'
    });
    
    // 获取当前语音设置
    const currentVoice = isCantonese.value 
      ? config.value.voices.cantonese.female 
      : config.value.voices.english.female
    
    // 根据当前情绪设置语音情感
    const emotion = getEmotionForTTS(currentEmotion.value)
    
    console.log('🎙️ 调用Azure TTS服务:', {
      text: text.length,
      voice: currentVoice,
      emotion: emotion,
      language: isCantonese.value ? 'Cantonese' : 'English'
    });
    
    // 使用真实的Azure TTS服务
    await azureTtsService.synthesizeAndPlay(
      text, 
      audioPlayer.value, 
      {
        voice: currentVoice,
        emotion: emotion,
        rate: 1.0,
        pitch: '0%'
      }
    )
    
    console.log('✅ Azure TTS播放完成')
    
  } catch (error) {
    console.error('❌ Azure TTS服务失败，使用后备方案:', error)
    
    // 后备方案：模拟音频播放时间
    const baseRate = isCantonese.value ? 60 : 50
    const estimatedDuration = Math.max(1500, text.length * baseRate)
    
    setTimeout(() => {
      onAudioEnded()
    }, estimatedDuration)
  }
}

// 将情绪状态转换为TTS情感参数
function getEmotionForTTS(emotion) {
  const emotionMap = {
    'cheerful': 'cheerful',
    'sad': 'sad', 
    'assistant': 'friendly',
    '': 'friendly'
  }
  return emotionMap[emotion] || 'friendly'
}

function updateEmotionBasedOnContent(content) {
  const lowerContent = content.toLowerCase()
  
  if (lowerContent.includes('sorry') || lowerContent.includes('唔好意思') || lowerContent.includes('unfortunately')) {
    currentEmotion.value = 'sad'
  } else if (lowerContent.includes('great') || lowerContent.includes('好') || lowerContent.includes('歡迎') || lowerContent.includes('excellent')) {
    currentEmotion.value = 'cheerful'
  } else if (lowerContent.includes('help') || lowerContent.includes('幫') || lowerContent.includes('服務') || lowerContent.includes('assist')) {
    currentEmotion.value = 'assistant'
  } else {
    currentEmotion.value = 'cheerful'
  }
}

function onAudioEnded() {
  currentAction.value = 'Idle'
  currentEmotion.value = ''
}

function scrollToBottom() {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

function showCloseConfirmation() {
  showConfirmModal.value = true
}

// 尚未开始对话时没有需要保留的内容，直接关闭；已开始则先确认
function requestClose() {
  if (hasStarted.value) {
    showCloseConfirmation()
  } else {
    emit('close')
  }
}

function handleEscape() {
  if (showConfirmModal.value) {
    hideConfirmation()
  } else {
    requestClose()
  }
}

function hideConfirmation() {
  showConfirmModal.value = false
}

function closeConversation() {
  // Reset all conversation state
  hasStarted.value = false
  messages.value = []
  userInput.value = ''
  showConfirmModal.value = false
  isProcessing.value = false
  isLoading.value = false
  currentAction.value = 'Idle'
  currentEmotion.value = ''
  
  // Stop any playing audio
  if (audioPlayer.value) {
    audioPlayer.value.pause()
    audioPlayer.value.src = ''
  }
  
  // 清理OpenAI对话歷史
  try {
    openaiChatService.clearHistory()
    console.log('🗑️ 已清理对话歷史')
  } catch (error) {
    console.warn('⚠️ 清理对话歷史失败:', error)
  }
  
  // Emit close event to parent
  emit('close')
}

function handleMessageAction(action) {
  if (action.type === 'link' && action.url) {
    // Use Vue Router for internal navigation
    window.open(action.url, '_blank')
  }
}

onBeforeUnmount(() => {
  if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus()
})

onMounted(async () => {
  previouslyFocused = document.activeElement
  nextTick(() => dialogRoot.value && dialogRoot.value.focus())
  // Initialize avatar in idle state
  currentAction.value = 'Idle'
  currentEmotion.value = ''
  
  // 验证API配置
  try {
    const isValid = validateApiConfig()
    if (isValid) {
      console.log('✅ API配置验证成功')
      console.log('📊 配置摘要:', getConfigSummary())
    }
  } catch (error) {
    console.warn('⚠️ API配置验证失败:', error.message)
    console.log('🔄 将使用后备服务方案')
  }
  
  // 验证Azure TTS配置
  try {
    azureTtsService.validateConfiguration()
    console.log('🎤 Azure TTS服务配置正常')
  } catch (error) {
    console.warn('⚠️ Azure TTS配置问题:', error.message)
  }
  
  // 验证OpenAI配置
  try {
    openaiChatService.validateConfiguration()
    console.log('🤖 OpenAI Chat服务配置正常')
  } catch (error) {
    console.warn('⚠️ OpenAI配置问题:', error.message)
  }
  
  // 在开发模式下运行API连通性测试
  if (API_CONFIG.debug.enableLogs) {
    console.log('🧪 运行API服务测试...')
    setTimeout(async () => {
      try {
        await apiTestSuite.testApiConnectivity()
      } catch (error) {
        console.warn('⚠️ API连通性测试失败:', error)
      }
    }, 2000) // 延迟2秒执行，确保组件完全加载
  }
})
</script>

<style scoped>
.avatar-chat {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  z-index: 1000;
  padding: var(--spacing-lg);
  overflow: auto;
}

.avatar-chat:focus {
  outline: none;
}

.chat-container {
  max-width: 1200px;
  margin: 0 auto;
  background: var(--background-white);
  border-radius: var(--radius-xl);
  padding: var(--spacing-2xl);
  box-shadow: var(--shadow-xl);
  min-height: calc(100vh - calc(var(--spacing-lg) * 2));
  display: flex;
  flex-direction: column;
}

.header-section {
  position: relative;
  margin-bottom: var(--spacing-2xl);
  text-align: center;
}

.header-section h2 {
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  font-size: var(--font-size-4xl);
  font-weight: 700;
}

.close-btn {
  position: absolute;
  top: 0;
  right: 0;
  background: #dc3545;
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-fast);
}

.close-btn:hover {
  background: #c82333;
  transform: scale(1.1);
}

.subtitle {
  color: var(--text-secondary);
  font-size: var(--font-size-xl);
  max-width: 600px;
  margin: 0 auto;
}

.chat-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-2xl);
  flex: 1;
  min-height: 600px;
}

.avatar-wrapper {
  background: var(--background-light);
  border-radius: var(--radius-lg);
  padding: var(--spacing-lg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.controls-panel {
  display: flex;
  flex-direction: column;
  background: var(--background-light);
  border-radius: var(--radius-lg);
  padding: var(--spacing-2xl);
}

.greeting-section {
  text-align: center;
  margin: auto 0;
}

.greeting-section h3 {
  color: var(--text-primary);
  margin-bottom: var(--spacing-lg);
  font-size: var(--font-size-2xl);
  font-weight: 600;
}

.greeting-section p {
  color: var(--text-secondary);
  margin-bottom: var(--spacing-2xl);
  line-height: 1.6;
  font-size: var(--font-size-lg);
}

.start-btn {
  background: var(--primary-text);
  color: white;
  border: none;
  padding: var(--spacing-lg) var(--spacing-2xl);
  border-radius: 50px;
  font-size: var(--font-size-xl);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-normal);
  box-shadow: var(--shadow-lg);
}

.start-btn:hover:not(:disabled) {
  background: var(--primary-text-hover);
  transform: translateY(-2px);
  box-shadow: var(--shadow-xl);
}

.start-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.conversation-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.messages {
  flex: 1;
  overflow-y: auto;
  margin-bottom: var(--spacing-lg);
  max-height: 400px;
  padding-right: var(--spacing-md);
}

.input-section {
  border-top: 1px solid #dee2e6;
  padding-top: var(--spacing-lg);
}

.input-group {
  display: flex;
  gap: var(--spacing-md);
  align-items: flex-end;
}

textarea {
  flex: 1;
  padding: var(--spacing-md) var(--spacing-lg);
  border: 2px solid #e9ecef;
  border-radius: var(--radius-lg);
  resize: none;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-sm);
  transition: var(--transition-fast);
}

textarea:focus {
  border-color: var(--primary-color);
}

textarea:disabled {
  background: var(--background-light);
  opacity: 0.7;
}

.control-buttons {
  display: flex;
  gap: var(--spacing-sm);
}

.lang-toggle {
  background: var(--secondary-color);
  color: white;
  border: none;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--radius-lg);
  cursor: pointer;
  font-weight: 600;
  transition: var(--transition-fast);
  min-width: 50px;
}

.lang-toggle:hover {
  background: var(--primary-text-hover);
}

.send-btn {
  background: var(--primary-text);
  color: white;
  border: none;
  padding: var(--spacing-md) var(--spacing-xl);
  border-radius: var(--radius-lg);
  cursor: pointer;
  font-weight: 600;
  transition: var(--transition-fast);
  min-width: 100px;
}

.send-btn:hover:not(:disabled) {
  background: var(--primary-text-hover);
}

.send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.confirm-modal {
  background: var(--background-white);
  border-radius: var(--radius-lg);
  padding: var(--spacing-2xl);
  max-width: 450px;
  width: 90%;
  box-shadow: var(--shadow-xl);
}

.confirm-modal h3 {
  color: var(--text-primary);
  margin: 0 0 var(--spacing-lg) 0;
  font-size: var(--font-size-2xl);
  text-align: center;
}

.confirm-modal p {
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-xl) 0;
  line-height: 1.6;
  text-align: center;
}

.modal-buttons {
  display: flex;
  gap: var(--spacing-lg);
  justify-content: center;
}

.modal-buttons button {
  padding: var(--spacing-md) var(--spacing-xl);
  border: none;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
  min-width: 120px;
}

.cancel-btn {
  background: #6c757d;
  color: white;
}

.cancel-btn:hover {
  background: #5a6268;
}

.confirm-btn {
  background: #dc3545;
  color: white;
}

.confirm-btn:hover {
  background: #c82333;
}

/* Responsive Design */
@media (max-width: 768px) {
  .avatar-chat {
    padding: var(--spacing-md);
  }
  
  .chat-container {
    padding: var(--spacing-lg) var(--spacing-md);
    margin: 0;
    border-radius: var(--radius-lg);
    min-height: calc(100vh - var(--spacing-lg));
  }
  
  .chat-section {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
    min-height: auto;
  }
  
  .avatar-wrapper {
    height: 250px;
    padding: var(--spacing-md);
  }
  
  .controls-panel {
    padding: var(--spacing-lg) var(--spacing-md);
  }
  
  .header-section h2 {
    font-size: var(--font-size-3xl);
    margin-bottom: var(--spacing-sm);
  }
  
  .subtitle {
    font-size: var(--font-size-base);
    margin-bottom: 0;
  }
  
  .header-section {
    margin-bottom: var(--spacing-lg);
  }
  
  .greeting-section h3 {
    font-size: var(--font-size-xl);
    margin-bottom: var(--spacing-md);
  }
  
  .greeting-section p {
    font-size: var(--font-size-sm);
    margin-bottom: var(--spacing-lg);
  }
  
  .start-btn {
    padding: var(--spacing-md) var(--spacing-2xl);
    font-size: var(--font-size-lg);
  }
  
  .messages {
    max-height: 300px;
    padding-right: var(--spacing-sm);
  }
  
  .input-group {
    flex-direction: column;
    gap: var(--spacing-md);
    align-items: stretch;
  }
  
  .control-buttons {
    justify-content: space-between;
  }
  
  textarea {
    min-height: 80px;
    font-size: 16px; /* Prevent zoom on iOS */
  }
  
  .lang-toggle, .send-btn {
    padding: var(--spacing-md) var(--spacing-xl);
    font-size: 16px;
    border-radius: var(--radius-md);
    flex: 1;
  }
  
  .close-btn {
    width: 35px;
    height: 35px;
    font-size: 20px;
    top: -5px;
    right: -5px;
  }
}
</style>